import { Box, ButtonBase, Typography } from '@mui/material';
import MenuRounded from '@mui/icons-material/MenuRounded';
import type { SvgIconComponent } from '@mui/icons-material';
import { MobileTab } from './mobileTabs';

const ACTIVE = '#F79420';
const IDLE = '#868686';

function TabButton({
  label,
  Icon,
  active,
  onClick,
  testId
}: {
  label: string;
  Icon: SvgIconComponent;
  active: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      data-testid={testId}
      sx={{
        flex: 1,
        minWidth: 0,
        height: 58,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '3px',
        color: active ? ACTIVE : IDLE,
        transition: 'transform 120ms ease, color 120ms ease',
        '&:active': { transform: 'scale(0.92)' }
      }}
    >
      {/* The pill behind the active icon (Material 3's active indicator). */}
      <Box
        sx={{
          width: 52,
          height: 28,
          borderRadius: '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: active ? 'rgba(247, 148, 32, 0.16)' : 'transparent',
          transition: 'background-color 160ms ease'
        }}
      >
        <Icon sx={{ fontSize: 23 }} />
      </Box>
      <Typography
        component="span"
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 10.5,
          lineHeight: '12px',
          fontWeight: active ? 600 : 500,
          color: 'inherit',
          px: 0.25,
          // Tab labels are kept short (mobileTabs.tsx) so they fit whole, never cut.
          whiteSpace: 'nowrap'
        }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
}

/**
 * The phone app's bottom navigation: the main destinations of the current
 * menu (see pickMobileTabs) plus Menu, which opens the full side drawer.
 * It sits in the layout's column under the scrolling content, so nothing
 * scrolls behind it, and pads itself above the home indicator.
 */
export default function MobileTabBar({
  tabs,
  activeRoute,
  menuActive,
  onSelect,
  onMenu
}: {
  tabs: MobileTab[];
  /** The tab whose destination the page is on, if any. */
  activeRoute?: string;
  /** The page is a menu destination that has no tab of its own. */
  menuActive: boolean;
  onSelect: (route: string) => void;
  onMenu: () => void;
}) {
  return (
    <Box
      component="nav"
      aria-label="Main"
      className="dosen-app-chrome"
      data-testid="mobile-tab-bar"
      sx={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'stretch',
        px: 0.5,
        pb: 'env(safe-area-inset-bottom)',
        bgcolor: '#0F0F0F',
        borderTop: '1px solid #2A2A2A'
      }}
    >
      {tabs.map((tab) => (
        <TabButton
          key={tab.route}
          label={tab.label}
          Icon={tab.Icon}
          active={tab.route === activeRoute}
          onClick={() => onSelect(tab.route)}
          testId={`mobile-tab-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
        />
      ))}
      <TabButton label="Menu" Icon={MenuRounded} active={menuActive} onClick={onMenu} testId="mobile-tab-menu" />
    </Box>
  );
}
